import type { AssetVariant } from "../lib/types.ts";

const ROOT = "assets";

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  return navigator.storage.getDirectory();
}

async function ensureDir(path: string[]): Promise<FileSystemDirectoryHandle> {
  let dir = await getRoot();
  for (const segment of path) {
    dir = await dir.getDirectoryHandle(segment, { create: true });
  }
  return dir;
}

export function assetPath(assetId: string, variant: AssetVariant): string {
  return `${ROOT}/${assetId}/${variant}`;
}

export async function writeBlob(path: string, blob: Blob): Promise<void> {
  const parts = path.split("/");
  const fileName = parts.pop();
  if (!fileName) throw new Error("Invalid path");
  const dir = await ensureDir(parts);
  const handle = await dir.getFileHandle(fileName, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export async function readBlob(path: string): Promise<Blob | null> {
  try {
    const parts = path.split("/");
    const fileName = parts.pop();
    if (!fileName) return null;
    let current = await getRoot();
    for (const segment of parts) {
      current = await current.getDirectoryHandle(segment);
    }
    const handle = await current.getFileHandle(fileName);
    return await handle.getFile();
  } catch {
    return null;
  }
}

function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "NotFoundError"
  );
}

export async function deletePath(path: string): Promise<void> {
  try {
    const parts = path.split("/");
    const fileName = parts.pop();
    if (!fileName || parts.length === 0) return;
    let current = await getRoot();
    for (const segment of parts.slice(0, -1)) {
      current = await current.getDirectoryHandle(segment);
    }
    const parentName = parts[parts.length - 1];
    if (!parentName) return;
    const parent = await current.getDirectoryHandle(parentName);
    await parent.removeEntry(fileName);
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
  }
}

export async function deleteAssetDir(assetId: string): Promise<void> {
  try {
    const root = await getRoot();
    const assetsDir = await root.getDirectoryHandle(ROOT);
    await assetsDir.removeEntry(assetId, { recursive: true });
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
  }
}

const objectUrlCache = new Map<string, string>();

export async function getObjectUrl(path: string): Promise<string | null> {
  const cached = objectUrlCache.get(path);
  if (cached) return cached;
  const blob = await readBlob(path);
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  objectUrlCache.set(path, url);
  return url;
}

export function revokeObjectUrl(path: string): void {
  const url = objectUrlCache.get(path);
  if (url) {
    URL.revokeObjectURL(url);
    objectUrlCache.delete(path);
  }
}

export function revokeAllObjectUrls(): void {
  for (const url of objectUrlCache.values()) URL.revokeObjectURL(url);
  objectUrlCache.clear();
}
